﻿import React, { useContext, useEffect, useState } from 'react';

import { AssignedAccessCode, Transaction, TransactionApi } from 'apis/TransactionApi';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDatePicker } from 'components/GMetrixDatePicker/GMetrixDatePicker';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { TransactionDetailsContext } from 'pages/access_codes/context';

import 'components/EditAccessCodesDialog/edit-access-codes-dialog.scss';

import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Container, Row } from 'react-bootstrap';
import { GenerateUUID, isOnOrBeforeDay } from 'utils';
import { useNotification } from 'app/notifications';

interface EditAccessCodesDialogProps {
	className?: string;
	editAccessCodeObj?: AssignedAccessCode;
	handleDuplicateTransaction: (dataItem: Transaction) => Promise<void>;
	onCancel: () => void;
	onSuccess: () => void;
	setGlobalAccessCodeList: (accessCodeList: AssignedAccessCode[]) => void;
	show: boolean;
	transactionId: number;
}

const defaultCreateAccessCodeObj: AssignedAccessCode = {
    AccessCodeTitle: '',
    Active: true,
    CreatedAt: new Date(),
    ExpirationDate: new Date(), // This is always updated by the first useEffect in this file.
    ExpirationDateString: '',
    ExpiresAfterDays: 0,
    Id: 0,
    Redeemed: 0,
    Remaining: undefined
};

export function EditAccessCodesDialog({
    editAccessCodeObj,
    handleDuplicateTransaction,
    onCancel,
    onSuccess,
    setGlobalAccessCodeList,
    show,
    transactionId
}: EditAccessCodesDialogProps) {

    const [errorMessage, setErrorMessage] = useState('');

    // const [hasSeatOrIndividual, setHasSeatOrIndividual] = useState(false);
    const { pageContext } = useContext(TransactionDetailsContext);
    const [accessCodeObj, setAccessCodeObj] = useState(defaultCreateAccessCodeObj);
    const [remainingIsValid, setRemainingIsValid] = useState(true);
    const { formatMessage } = useIntl();
    const { catchWithToast } = useNotification();
    const [accessCodeTitleValid, setAccessCodeTitleValid] = useState(true);
    const [createSingleUseCodeCheck, setCreateSingleUseCodeCheck] = useState(false);

    useEffect(() => {

        if (!show) {

            setAccessCodeTitleValid(true);
            setRemainingIsValid(true);
            setErrorMessage('');
            setAccessCodeObj(defaultCreateAccessCodeObj);
            if (pageContext?.Data.mostRestrictiveLicense?.license) {

                setAccessCodeObj({
                    ...defaultCreateAccessCodeObj,
                    ExpirationDate: pageContext.Data.mostRestrictiveLicense.license.OrderExpireDate,
                    ExpirationDateString: pageContext.Data.mostRestrictiveLicense.license.OrderExpireDateString
                });

            }

        } else {

            if (editAccessCodeObj) {

                setAccessCodeObj(editAccessCodeObj);

            }

        }

    }, [show, pageContext?.Data.mostRestrictiveLicense]);

    const createCodeOnClick = () => {

        if (
            accessCodeObj.ExpirationDateString.length === 0 ||
			(accessCodeObj.ExpirationDate && new Date() === new Date(accessCodeObj.ExpirationDate))
        ) {

            setErrorMessage(formatMessage({ id: 'please_select_an_expiration_date_in_the.342df525', defaultMessage: 'Please select an expiration date in the future.' }));
            return;

        }
        if (accessCodeObj.AccessCodeTitle.length > 8) {

            setErrorMessage(formatMessage({ id: 'identifier_field_must_be_8_characters_or.76a12756', defaultMessage: 'Identifier field must be 8 characters or less.' }));
            setAccessCodeTitleValid(accessCodeObj.AccessCodeTitle.length < 8);
            return;

        } else {

            setAccessCodeTitleValid(true);

        }
        if (accessCodeObj.AccessCodeTitle.length === 0) {

            setAccessCodeTitleValid(accessCodeObj.AccessCodeTitle.length !== 0);
            setErrorMessage(formatMessage({ id: 'identifier_field_must_be_longer_than_zer.397486d9', defaultMessage: 'Identifier field must be longer than zero characters.' }));
            return;

        } else {

            setAccessCodeTitleValid(true);

        }
        if (pageContext?.Data.mostRestrictiveLicense && accessCodeObj.ExpirationDate) {

            TransactionApi.CreateAccessCodeForTransaction(
                transactionId,
                accessCodeObj.AccessCodeTitle,
                4,
                365,
                accessCodeObj.ExpirationDate,
                accessCodeObj.Remaining !== undefined
                    ? accessCodeObj.Remaining
                    : pageContext?.Data?.mostRestrictiveLicense?.avaliableStudentsAllowed !== -1
                        ? pageContext?.Data?.mostRestrictiveLicense?.avaliableStudentsAllowed
                        : 999,
                createSingleUseCodeCheck
            )
                .then(res => {

                    if (res.Done) {

                        TransactionApi.GetAccessCodesByTransactionId(transactionId).then(accessCodeList => {

                            setGlobalAccessCodeList(accessCodeList);
                            onSuccess();

                        }).catch((error: unknown) => catchWithToast(error, formatMessage({ id: 'failed_to_load_access_codes.242fe9e5', defaultMessage: 'Failed to load access codes.' })));

                    } else {

                        setErrorMessage(res.Message);

                    }

                })
                .catch((error: unknown) => catchWithToast(error, formatMessage({ id: 'failed_to_create_access_code.615cc829', defaultMessage: 'Failed to create access code.' })));

        }

    };

    const editCodeOnClick = async () => {

        if (
            accessCodeObj.ExpirationDateString.length === 0 ||
			(accessCodeObj.ExpirationDate && new Date() === new Date(accessCodeObj.ExpirationDate))
        ) {

            setErrorMessage(formatMessage({ id: 'please_select_an_expiration_date_in_the.342df525', defaultMessage: 'Please select an expiration date in the future.' }));
            return false;

        }

        try {

            await TransactionApi.UpdateOneAccessCodeInTransaction(accessCodeObj);
            const accessCodeList = await TransactionApi.GetAccessCodesByTransactionId(transactionId);
            setGlobalAccessCodeList(accessCodeList);
            onSuccess();

        } catch (e) {

            catchWithToast(e, formatMessage({ id: 'failed_to_update_access_code.29142351', defaultMessage: 'Failed to update access code.' }));

        }

    };

    return (
        <GMetrixDialog
            actionButtonClick={editAccessCodeObj ? editCodeOnClick : createCodeOnClick}
            actionButtonDisabled={false}
            actionButtonText={`${editAccessCodeObj ? formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' }) : formatMessage({ id: 'create.be56b47d', defaultMessage: 'Create' })}`}
            dialogLabel={`${editAccessCodeObj ? formatMessage({ id: 'edit_code.7f9cab9b', defaultMessage: 'Edit Code' }) : formatMessage({ id: 'create_new_code.694d4a50', defaultMessage: 'Create New Code' })}`}
            onCancel={onCancel}
            show={show}
        >
            <Container fluid className="edit-access-codes-dialog">
                <Row>
                    {errorMessage.length > 0 && (
                        <Col
                            className="mt-2 text-center edit-access-codes-dialog text-error"
                            lg={12}
                            md={12}
                            sm={12}
                            xl={12}
                            xs={12}
                        >
                            {errorMessage}
                        </Col>
                    )}
                    {!editAccessCodeObj && (
                        <Col className="mt-2 edit-access-codes-dialog" lg={12} md={12} sm={12} xl={12} xs={12}>
                            <FormattedMessage defaultMessage={'You can create new codes that will allow students to access the content in this Access Code.\r\n							Their data will also be included in the reports on this Access Code.'} id="you_can_create_new_codes_that_will_allow.ffcd21a1" />
                            <br />
                            <br />
                            <FormattedMessage defaultMessage={'If your intent is to create a new Access Code with reports for a new group of students, then\r\n							please'} id="if_your_intent_is_to_create_a_new_access.5d2041ef" />
                            {' '}
                            <a
                                onClick={() => {

                                    onCancel();
                                    pageContext?.Data.transactionDetails &&
										handleDuplicateTransaction(pageContext.Data.transactionDetails);

                                }}
                                href="#"
                            >
                                <FormattedMessage defaultMessage="Duplicate" id="duplicate.9ae890dd" />
                            </a>
                            {' '}
                            <FormattedMessage defaultMessage="this Access Code instead." id="this_access_code_instead.4037c97b" />
                        </Col>
                    )}

                    <Col className="mt-2 d-flex align-items-end" xl={12}>
                        <GMetrixInput
                            disabled={editAccessCodeObj !== undefined}
                            label={formatMessage({ id: 'identifier_8_characters_or_less.896baeb1', defaultMessage: 'Identifier (8 characters or less)' })}
                            onChange={e => setAccessCodeObj({ ...accessCodeObj, AccessCodeTitle: e.target.value })}
                            value={accessCodeObj.AccessCodeTitle}
                        />
                        {!editAccessCodeObj && (
                            <GMetrixButton
                                onClick={() => setAccessCodeObj({
                                    ...accessCodeObj,
                                    AccessCodeTitle: GenerateUUID().substring(0, 8)
                                })} className="ms-3"
                                variant="primary"
                            >
                                <FormattedMessage defaultMessage="Randomize" id="randomize.4b7ca16f" />
                            </GMetrixButton>
                        )}
                    </Col>
                    {editAccessCodeObj && (
                        <Col className="mt-2" xl={12}>
                            <GMetrixCheckInput
                                checked={accessCodeObj.Active}
                                label={formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' })}
                                onChange={e => setAccessCodeObj({ ...accessCodeObj, Active: e.target.checked })}
                                type="switch"
                            />
                        </Col>
                    )}
                    {editAccessCodeObj && (
                        <Col className="mt-2" xl={12}>
                            <GMetrixInput disabled label={formatMessage({ id: 'redeemed.03edbd00', defaultMessage: 'Redeemed' })} value={accessCodeObj.Redeemed} />
                        </Col>
                    )}
                    <Col className="mt-2" xl={12}>
                        <GMetrixInput
                            max={
                                createSingleUseCodeCheck
                                    ? 100
                                    : pageContext?.Data.transactionDetails?.IsUnlimited
                                        ? 999
                                        : pageContext?.Data?.mostRestrictiveLicense?.avaliableStudentsAllowed
                            }
                            onChange={e => {

                                const amount = parseInt(e.target.value);
                                if (
                                    pageContext?.Data.mostRestrictiveLicense &&
									pageContext?.Data.transactionDetails?.IsUnlimited &&
									pageContext?.Data.mostRestrictiveLicense.avaliableStudentsAllowed <
										parseInt(e.target.value)
                                ) {

                                    setErrorMessage(
                                        formatMessage({ id: 'max_redemptions_cant_be_higher_than_the.13176ada', defaultMessage: 'Max Redemptions can\'t be higher than the uses remaining on your license' })
                                    );
                                    setRemainingIsValid(false);

                                } else {

                                    setErrorMessage('');
                                    setRemainingIsValid(true);

                                }
                                if (createSingleUseCodeCheck) {

                                    if (amount > 100) {

                                        setAccessCodeObj({
                                            ...accessCodeObj,
                                            Remaining: 100
                                        });

                                    } else if (amount < 1) {

                                        setAccessCodeObj({
                                            ...accessCodeObj,
                                            Remaining: 1
                                        });

                                    } else {

                                        setAccessCodeObj({
                                            ...accessCodeObj,
                                            Remaining: amount
                                        });

                                    }

                                } else {

                                    if (amount > 999) {

                                        setAccessCodeObj({
                                            ...accessCodeObj,
                                            Remaining: 999
                                        });

                                    } else if (amount < 1) {

                                        setAccessCodeObj({
                                            ...accessCodeObj,
                                            Remaining: 1
                                        });

                                    } else {

                                        setAccessCodeObj({
                                            ...accessCodeObj,
                                            Remaining: amount
                                        });

                                    }

                                }

                            }}
                            label={formatMessage({ id: 'max_redemptions.af607a43', defaultMessage: 'Max Redemptions' })}
                            type="number"
                            value={accessCodeObj.Remaining ?? undefined}
                        />
                    </Col>
                    <Col className="mt-2 mb-4" xl={12} >
                        <GMetrixDatePicker
                            onChange={(date: any) => {

                                // If they choose an expiration date past the most restrictive license on this transaction it will just set it to the most restrictive licenses expiration date.
                                const selectedDate = date;
                                if (selectedDate &&
                                    pageContext?.Data.mostRestrictiveLicense &&

                                    // Day-precision compare: the license expiration day itself is allowed.
                                    isOnOrBeforeDay(selectedDate, pageContext?.Data.mostRestrictiveLicense?.license.OrderExpireDate)) {

                                    setAccessCodeObj({
                                        ...accessCodeObj,
                                        ExpirationDate: selectedDate ? new Date(selectedDate) : undefined,
                                        ExpirationDateString: selectedDate ?? ''
                                    });

                                } else {

                                    setErrorMessage(formatMessage({ id: 'you_cannot_set_the_expiration_date_past.7b8c808d', defaultMessage: 'You cannot set the expiration date past the most restrictive license date' }));
                                    if (pageContext?.Data.mostRestrictiveLicense) {

                                        setAccessCodeObj({
                                            ...accessCodeObj,
                                            ExpirationDate: new Date(
                                                pageContext?.Data.mostRestrictiveLicense.license.OrderExpireDate
                                            ),
                                            ExpirationDateString: pageContext?.Data.mostRestrictiveLicense.license.OrderExpireDateString
                                        });

                                    }

                                }

                            } }
                            value={accessCodeObj.ExpirationDateString.length > 0 && accessCodeObj.ExpirationDate
                                ? new Date(accessCodeObj.ExpirationDate)
                                : new Date()}
                            label={formatMessage({ id: 'redemption_expiration_date.d1e3b886', defaultMessage: 'Redemption Expiration Date' })}
                        />

                    </Col>
                    {!editAccessCodeObj && (
                        <Col className="mt-2 mb-4">
                            <GMetrixCheckInput
                                checked={createSingleUseCodeCheck}
                                label={formatMessage({ id: 'create_single_use_codes.9036f4ce', defaultMessage: 'Create Single-Use Codes' })}
                                onChange={e => setCreateSingleUseCodeCheck(e.target.checked)}
                                type={'checkbox'}
                            />
                        </Col>
                    )}
                </Row>
            </Container>
        </GMetrixDialog>
    );

}
